import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRestaurant, Restaurant } from '@/shared/services/restaurantsService';
import { getMenu, MenuCategory, MenuProduct } from '@/shared/services/menusService';
import { ImageCarousel } from '@/shared/components/ImageCarousel';
import styles from './RestaurantDetailPage.module.css';

export const RestaurantDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
    const [categories, setCategories] = useState<MenuCategory[]>([]);
    const [products, setProducts] = useState<MenuProduct[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch restaurant details and menu concurrently
            const [restaurantData, menuData] = await Promise.all([
                getRestaurant(id!),
                getMenu(id!)
            ]);

            setRestaurant(restaurantData);
            setCategories(menuData.categories);
            setProducts(menuData.products);
        } catch (err) {
            console.error('Error fetching restaurant details:', err);
            setError('Error al cargar los detalles del restaurante');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp?: { seconds: number; nanoseconds: number }) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getProductsByCategory = (categoryId: string) => {
        return products.filter(p => p.categoryId === categoryId && p.status === 'activo');
    };

    if (loading) {
        return <div className={styles.loadingOverlay}>Cargando detalles del comercio...</div>;
    }

    if (error || !restaurant) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>{error || 'Comercio no encontrado'}</div>
                <button onClick={() => navigate('/restaurants')} className={styles.backButton}>
                    ← Volver
                </button>
            </div>
        );
    }

    const activeCategories = (categories || []).filter(c => c.status === 'activa').sort((a, b) => a.displayOrder - b.displayOrder);
    const totalActiveProducts = (products || []).filter(p => p.status === 'activo').length;

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div className={styles.headerTitle}>
                    <button className={styles.backButton} onClick={() => navigate('/restaurants')}>
                        ← Volver
                    </button>
                    <h1>{restaurant.name}</h1>
                    <span className={`${styles.statusBadge} ${styles[`status${restaurant.status}`]}`}>
                        {restaurant.status}
                    </span>
                </div>
            </header>

            <div className={styles.summaryCards}>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Estado Operativo</span>
                    <span className={`${styles.cardValue} ${restaurant.operationalStatus === 'abierto' ? styles.statusOpen : styles.statusClosed}`}>
                        {restaurant.operationalStatus === 'abierto' ? '🟢 Abierto' : '🔴 Cerrado'}
                    </span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Categorías</span>
                    <span className={styles.cardValue}>{activeCategories.length}</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Productos</span>
                    <span className={styles.cardValue}>{totalActiveProducts}</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Fecha de Creación</span>
                    <span className={styles.cardValue} style={{ fontSize: '1rem' }}>
                        {formatDate(restaurant.createdAt)}
                    </span>
                </div>
            </div>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>📋 Información General</h2>
                <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Email:</span>
                        <span className={styles.infoValue}>{restaurant.email}</span>
                    </div>
                    <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Teléfono:</span>
                        <span className={styles.infoValue}>{restaurant.phone}</span>
                    </div>
                    <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Dirección:</span>
                        <span className={styles.infoValue}>{restaurant.address.text}</span>
                    </div>
                    {restaurant.slogan && (
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Slogan:</span>
                            <span className={styles.infoValue}>{restaurant.slogan}</span>
                        </div>
                    )}
                </div>
                {restaurant.images && restaurant.images.length > 0 && (
                    <div className={styles.imagesContainer}>
                        <span className={styles.infoLabel}>Imágenes:</span>
                        <ImageCarousel images={restaurant.images} maxImages={3} />
                    </div>
                )}
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>🚚 Configuración de Delivery</h2>
                <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Tipo de Delivery:</span>
                        <span className={styles.infoValue}>
                            {restaurant.delivery.type === 'plataforma' ? 'Plataforma' : 'Propio'}
                        </span>
                    </div>
                    <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Tarifa Base:</span>
                        <span className={styles.infoValue}>Bs. {restaurant.delivery.baseFee.toFixed(2)}</span>
                    </div>
                    {restaurant.delivery.minimumOrder !== undefined && (
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Pedido Mínimo:</span>
                            <span className={styles.infoValue}>Bs. {restaurant.delivery.minimumOrder.toFixed(2)}</span>
                        </div>
                    )}
                    {restaurant.delivery.coverageRadiusKm !== undefined && (
                        <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Radio de Cobertura:</span>
                            <span className={styles.infoValue}>{restaurant.delivery.coverageRadiusKm} km</span>
                        </div>
                    )}
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>🍽️ Menú ({activeCategories.length} Categorías, {totalActiveProducts} Productos)</h2>
                {activeCategories.length === 0 ? (
                    <p className={styles.emptyMessage}>Este restaurante aún no tiene categorías en su menú.</p>
                ) : (
                    <div className={styles.menuContainer}>
                        {activeCategories.map((category) => {
                            const categoryProducts = getProductsByCategory(category.categoryId);
                            return (
                                <div key={category.categoryId} className={styles.categoryCard}>
                                    <div className={styles.categoryHeader}>
                                        <h3 className={styles.categoryName}>{category.name}</h3>
                                        <span className={styles.productCount}>{categoryProducts.length} productos</span>
                                    </div>
                                    {categoryProducts.length === 0 ? (
                                        <p className={styles.emptyProducts}>No hay productos en esta categoría</p>
                                    ) : (
                                        <div className={styles.productsGrid}>
                                            {categoryProducts.map((product) => (
                                                <div key={product.productId} className={styles.productCard}>
                                                    {product.images && product.images.length > 0 && (
                                                        <img
                                                            src={product.images[0]}
                                                            alt={product.name}
                                                            className={styles.productImage}
                                                        />
                                                    )}
                                                    <div className={styles.productInfo}>
                                                        <h4 className={styles.productName}>{product.name}</h4>
                                                        {product.description && (
                                                            <p className={styles.productDescription}>{product.description}</p>
                                                        )}
                                                        <div className={styles.productFooter}>
                                                            <span className={styles.productPrice}>Bs. {product.price.toFixed(2)}</span>
                                                            <span className={`${styles.availabilityBadge} ${product.availability === 'disponible' ? styles.available : styles.outOfStock}`}>
                                                                {product.availability === 'disponible' ? 'Disponible' : 'Agotado'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
};
